import React, { useEffect } from "react";
import { IndividualService } from "../../../Component/Api/Individual/IndividualService";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption, SortDirections } from '../../../Component/Model/Dto/ColumnSortOption';
import { Individual } from "../../../Component/Model/Dto/Individual";
import { defaultObject } from "../../../Component/Model/helpers";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { IndividualModal } from "../Individual/IndividualModal";
import { EmployeeStructureTableMapper } from "./EmployeeStructureColumn";
import { OccupationalLevelList } from "../../../Component/Model/Enum/MultiSelectOptions/OccupationallevelOptions";
import { getDateWithoutTime } from "../../../Helpers/DateFormatter";


interface IProps {
    scorecardId: string;
    filterDate?: Date;
    filterByDate: boolean;
    occupationIds?: number[];
    raceIds?: number[];
    genderIds?: number[];
    onlyDisabled?: boolean;
    isPermanent?: boolean | null;
    userSelectedDate?: Date;
    sourceScorecardIds?: string[];
    filterByEePlanDate?: boolean;
    filterStartDate? : Date;
    filterEndDate? : Date;
}

export const EmployeeStructureTable = (props: IProps) => {

    const [objectivesData, setObjectivesData] = React.useState<Individual.SummaryDtoIn[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showIndividualModal, setShowIndividualModal] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [selectedIndividualId, setSelectedIndividualId] = React.useState<number>(0);
    const [sortColumnNames, setSortColumnNames] = React.useState<string[]>([]);
    const [sortDirections, setSortDirections] = React.useState<SortDirections[]>([]);


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setSortColumnNames([sortOption.ColumnName]);
        setSortDirections([sortOption.SortDirection]);
        setPageNumber(1);
    }

    const closeIndividualModal = () => {
        setShowIndividualModal(false);
        setSelectedIndividualId(0);
    }

    const individualsSearchRequest: Individual.SummarySearchRequest = {
        PageNumber: pageNumber as number,
        PageSize: entriesPerPage.defaultValue as number,
        ScorecardId: props.scorecardId,
        EmployeeTypeIds: [1, 4],
        FilterByDate: props.filterByDate,
        FilterDate: props.filterDate,
        FilterForBoardParticipation: true,
        FilterForEmployeeStructure: true,
        OccupationIds: props.occupationIds,
        RaceIds: props.raceIds,
        GenderIds: props.genderIds,
        SearchValue: searchValue,
        SortColumnNames: sortColumnNames,
        SortDirections: sortDirections,
        UserSelectedDate: props.userSelectedDate || props.filterDate || getDateWithoutTime(new Date()),
        OnlyDisabled: props.onlyDisabled,
        IsPermanent: props.isPermanent,
        SourceScorecardIds: props.sourceScorecardIds || undefined,
        FilterByEePlanDate: props.filterByEePlanDate || false,
        HireDateFilterStart: props.filterStartDate || undefined,
        HireDateFilterEnd: props.filterEndDate || undefined
    };

    const fetchData = async () => {
        setIsLoading(true);

        if (individualsSearchRequest.OccupationIds?.length === 0 ){
            OccupationalLevelList().map((item: any) =>
                individualsSearchRequest.OccupationIds?.push(parseInt(item.value))
            );    
        }

        try {
            let searchResult = await IndividualService.SummarySearchIndividuals(individualsSearchRequest);
            if (searchResult.data) {
                setObjectivesData(searchResult.data.dataObject);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };
    const onEdit = (id: number) => {
        setSelectedIndividualId(id);
        setShowIndividualModal(true);
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue, sortColumnNames, sortDirections]); // eslint-disable-line react-hooks/exhaustive-deps

    const employeeTableData = EmployeeStructureTableMapper(objectivesData, onEdit);

    return (
        <>
            <div style={{ marginTop: "-32px" }}>
                <DataTable
                    table={employeeTableData}
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    isLoading={isLoading}
                    canSearch={true}
                    searchValueChanged={searchValueChanged}
                    manualSorting={true}
                    columnSortingChanged={columnSortingChanged}
                ></DataTable>
            </div>

            {showIndividualModal && <IndividualModal showIndividualModal={showIndividualModal} closeIndividualModal={closeIndividualModal} scorecardId={props.scorecardId} id={selectedIndividualId} />}

        </>
    );
}
